import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useApp } from "../state/app";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { fmtD, fmtDT, fmtT, isoToSec, secToIso, type PeriodSel } from "../utils/period";
import { toStation } from "../utils/date";
import type { FileRow } from "../types";

type Src = "all" | "g1" | "g2" | "pa";

const COLOR: Record<string, string> = { g1: "#2a78d6", g2: "#eb6834", pa: "#1baf7a" };
const fmtInt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");   // no line break inside a number
const hhmm = (iso: string) => fmtT(iso).slice(0, 5);

const srcOf = (f: FileRow): "g1" | "g2" | "pa" => (f.kind === "meter" ? "pa" : f.unit === 2 ? "g2" : "g1");

/** «14.08.2025 14:50 → 21.08.2025 11:18» — always with the year (old exports come in too). */
export function filePeriod(f: FileRow): string {
  return f.from_ts.slice(0, 10) === f.to_ts.slice(0, 10)
    ? `${fmtD(f.from_ts)} ${hhmm(f.from_ts)} → ${hhmm(f.to_ts)}`
    : `${fmtD(f.from_ts)} ${hhmm(f.from_ts)} → ${fmtD(f.to_ts)} ${hhmm(f.to_ts)}`;
}

/** The period cell of the upload history tables: two lines, «from →» and «to», with the year. */
export function HistoryPeriod({ from, to }: { from: string | null; to: string | null }) {
  const nowrap = { whiteSpace: "nowrap" } as const;
  if (!from) return <>—</>;
  return (
    <>
      <div style={nowrap}><bdi dir="ltr">{fmtDT(from).slice(0, 16)}{to ? " →" : ""}</bdi></div>
      {to && <div style={nowrap}>{fmtDT(to).slice(0, 16)}</div>}
    </>
  );
}

// «Телеметрия» on exactly the file's period: G1/G2 store the last second, the meter its last
// minute (→ +59 s, the period's «to» is the last second inclusive).
function fileSel(f: FileRow): PeriodSel {
  const to = f.kind === "meter" ? secToIso(isoToSec(f.to_ts) + 59) : f.to_ts;
  return { kind: "file", from: f.from_ts, to, file: { id: f.id, filename: f.filename, unit: f.unit, from: f.from_ts, to } };
}

export function useOpenFile() {
  const app = useApp();
  return (f: FileRow) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    app.openTelemetry(srcOf(f), fileSel(f));
  };
}

/** One upload: name and source, the data period (with the year), when and by whom it was uploaded. */
export function FileItem({ f, onOpen, divider = true }: { f: FileRow; onOpen: (f: FileRow) => void; divider?: boolean }) {
  const { t } = useT();
  const src = srcOf(f);
  const what = f.status === "skipped" ? t("files.repeat") : t("files.new_rows", { n: fmtInt(f.rows ?? 0) });
  return (
    <div onClick={() => onOpen(f)} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "start", padding: "8px 0", borderTop: divider ? "1px solid var(--border)" : undefined, cursor: "pointer" }}>
      <div style={{ minWidth: 0 }}>
        {/* file names and «from → to» read left to right in Farsi too (otherwise the arrow points back) */}
        <div style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📁 <bdi dir="ltr">{f.filename}</bdi></div>
        <div style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", margin: "1px 0" }}><bdi dir="ltr">{filePeriod(f)}</bdi></div>
        <div style={{ fontSize: 11, color: "var(--hint)" }}>
          {t("files.uploaded")} {fmtDT(toStation(f.created_at)).slice(0, 16)}{f.by_name ? ` · ${f.by_name}` : ""} · {what}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: COLOR[src], border: `1px solid ${COLOR[src]}`, borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap" }}>
        {src === "pa" ? t("telemetry.src_pa") : src.toUpperCase()}
      </span>
    </div>
  );
}

/** «все» on the «Последние файлы» card: every upload, newest first, filtered by source. */
export function FilesSheet({ onClose, hasUnits, hasPa }: { onClose: () => void; hasUnits: boolean; hasPa: boolean }) {
  const { t } = useT();
  const open = useOpenFile();
  const [src, setSrc] = useState<Src>("all");
  const [rows, setRows] = useState<FileRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useTelegramBackButton(onClose, [onClose]);

  useEffect(() => {
    setRows(null); setErr(null);
    api.get<FileRow[]>(`/api/home/files?source=${src}`).then(setRows).catch((e: ApiError) => setErr(e.message));
  }, [src]);

  const chips: [Src, string][] = [
    ["all", t("files.f_all")],
    ...(hasUnits ? [["g1", "G1"], ["g2", "G2"]] as [Src, string][] : []),
    ...(hasPa ? [["pa", t("telemetry.src_pa")]] as [Src, string][] : []),
  ];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      {/* a fixed minimum height keeps the source chips in place when a filter has few files */}
      <div style={{ background: "var(--bg)", width: "100%", minHeight: "70vh", maxHeight: "92vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, marginBottom: 4 }}>{t("files.all_title")}</h2>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 10 }}>{t("files.hint")}</div>
        {chips.length > 2 && (
          <div className="turbine-switcher">
            {chips.map(([k, label]) => (
              <button key={k} className={"turbine-chip" + (src === k ? " active" : "")}
                onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setSrc(k); }}>
                <span className="turbine-chip-name">{label}</span>
              </button>
            ))}
          </div>
        )}
        {err && <div className="alert error">{err}</div>}
        {!rows && !err && <div className="empty-state"><span className="spinner" /></div>}
        {rows && rows.length === 0 && <div className="empty-state">{t("files.none")}</div>}
        {rows && rows.length > 0 && (
          <div className="card">
            {rows.map((f, i) => <FileItem key={f.kind + f.id} f={f} divider={i > 0} onOpen={x => { open(x); onClose(); }} />)}
          </div>
        )}
        <button className="btn secondary" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}
